/**
 * Employee API Service
 * Handles employee-related API calls to the backend
 */

import { apiGet, apiPost, apiPut, apiDelete, apiPostFormData } from '@/shared/api/apiClient';
import type { GetEmployeesParams } from './employeeManagementService';

/**
 * Employee DTO from Backend
 */
export interface EmployeeDto {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  dateOfBirth: string;
  genderId: string;
  genderValue: string;
  maritalStatusId: string;
  maritalStatusValue: string;
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  jobTitle: string;
  departmentId: string;
  departmentName?: string;
  roleId?: string;
  roleName?: string;
  managerId?: string;
  managerName?: string;
  joiningDate: string;
  employmentTypeId: string;
  employmentTypeValue: string;
  statusId: string;
  statusValue: string;
  salary: number;
  currency: string;
  photoUrl?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}


/**
 * Get employee by ID
 * @param employeeId Employee UUID
 * @param tenantId Tenant UUID
 * @returns Employee details
 */
export async function getEmployeeById(employeeId: string, tenantId: string): Promise<EmployeeDto> {
  const endpoint = `/employees/${employeeId}/details?tenantId=${tenantId}`;
  return apiGet<EmployeeDto>(endpoint);
}

/**
 * Bank Details DTO
 */
export interface BankDetailsDto {
  id: string;
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  branchName?: string;
  accountTypeId: string;
  accountTypeValue: string;
  isPrimary: boolean;
  isVerified: boolean;
  aadharNumber?: string;
  panNumber?: string;
  createdAt: string;
}

/**
 * Emergency Contact DTO
 */
export interface EmergencyContactDto {
  id: string;
  contactName: string;
  relationship: string;
  phoneNumber: string;
  alternatePhone?: string;
  email?: string;
  address?: string;
  isPrimary: boolean;
  createdAt: string;
}

/**
 * Employee Details DTO (comprehensive)
 */
export interface EmployeeDetailsDto extends EmployeeDto {
  bloodGroup?: string;
  nationality?: string;
  personalEmail?: string;
  personalPhoneNumber?: string;
  currentAddress?: string;
  permanentAddress?: string;
  isOnboardingComplete: boolean;
  probationStartDate?: string;
  probationEndDate?: string;
  isProbationComplete: boolean;
  probationDurationDays?: number;
  bankDetails: BankDetailsDto[];
  emergencyContacts: EmergencyContactDto[];
  manager?: {
    id: string;
    employeeCode: string;
    fullName: string;
    email: string;
    jobTitle?: string;
    departmentName?: string;
    photoUrl?: string;
  };
  experiences: EmployeeExperienceDto[];
}

/**
 * React Query key factory for employee data, mirroring `ticketKeys` in
 * features/tickets/services/ticket.service.ts.
 */
export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (params: GetEmployeesParams) => [...employeeKeys.lists(), params] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (employeeId: string, tenantId: string) => [...employeeKeys.details(), employeeId, tenantId] as const,
  jobHistory: (employeeId: string) => [...employeeKeys.all, 'jobHistory', employeeId] as const,
  managerOptions: (search: string, take: number) => [...employeeKeys.all, 'managerOptions', search, take] as const,
  jobTitleSuggestions: (search: string, take: number) => [...employeeKeys.all, 'jobTitleSuggestions', search, take] as const,
  orgTree: (departmentId: string | undefined, includeTerminated: boolean) =>
    [...employeeKeys.all, 'orgTree', departmentId, includeTerminated] as const,
} as const;

/**
 * Get comprehensive employee details
 * @param employeeId Employee UUID
 * @param tenantId Tenant UUID
 * @returns Comprehensive employee details including bank details
 */
export async function getEmployeeDetails(employeeId: string, tenantId: string): Promise<EmployeeDetailsDto> {
  const endpoint = `/employees/${employeeId}/details?tenantId=${tenantId}`;
  return apiGet<EmployeeDetailsDto>(endpoint);
}

/**
 * Map backend employee DTO to frontend User format
 */
export function mapEmployeeDtoToUser(employee: EmployeeDto) {
  return {
    id: employee.id,
    name: employee.fullName,
    email: employee.email,
    designation: employee.jobTitle,
    department: employee.departmentName || employee.jobTitle,
    avatar: employee.photoUrl,
    employeeId: employee.employeeCode,
    joiningDate: new Date(employee.joiningDate).toISOString().split('T')[0],
    phone: employee.phoneNumber,
    location: employee.city || employee.state || 'Office',
    manager: employee.managerName,
    status: employee.statusValue === 'Active' ? 'active' :
            employee.statusValue === 'OnLeave' ? 'on-leave' : 'inactive',
  };
}

/**
 * Update employee personal information
 * @param employeeId Employee UUID
 * @param data Personal info update data
 * @returns Success or error response
 */
export interface UpdateEmployeePersonalInfoDto {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  maritalStatusId: string;
  /** ISO date (yyyy-MM-dd). Omit to leave unchanged. */
  dateOfBirth?: string;
  genderId?: string;
  bloodGroup?: string;
  nationality?: string;
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export async function updateEmployeePersonalInfo(
  employeeId: string,
  data: UpdateEmployeePersonalInfoDto
): Promise<void> {
  return apiPut<void>(`/employees/${employeeId}/personal-info`, data);
}

/**
 * Employee Experience DTOs
 */
export interface EmployeeExperienceDto {
  id: string;
  companyName: string;
  designation: string;
  description?: string;
  startMonthYear: string;
  endMonthYear: string;
}

export interface AddEmployeeExperienceDto {
  companyName: string;
  designation: string;
  description?: string;
  startMonthYear: string;
  endMonthYear: string;
}

export interface UpdateEmployeeExperienceDto extends AddEmployeeExperienceDto {
}

export async function addEmployeeExperience(
  employeeId: string,
  tenantId: string,
  data: AddEmployeeExperienceDto
): Promise<{ id: string }> {
  return apiPost<{ id: string }>(`/employees/${employeeId}/experiences?tenantId=${tenantId}`, data);
}

export async function updateEmployeeExperience(
  employeeId: string,
  experienceId: string,
  data: UpdateEmployeeExperienceDto
): Promise<void> {
  return apiPut<void>(`/employees/${employeeId}/experiences/${experienceId}`, data);
}

export async function deleteEmployeeExperience(
  employeeId: string,
  experienceId: string
): Promise<void> {
  return apiDelete<void>(`/employees/${employeeId}/experiences/${experienceId}`);
}

export async function uploadEmployeeDocument(
  employeeId: string,
  file: File,
  documentType: string,
  description?: string
): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);
  if (description) {
    formData.append('description', description);
  }

  return apiPostFormData<any>(`/employees/${employeeId}/documents/upload`, formData);
}

export async function getEmployeeDocuments(
  employeeId: string
): Promise<any[]> {
  return apiGet<any[]>(`/employees/${employeeId}/documents`);
}

export async function refreshEmployeeDocumentUrls(
  employeeId: string,
  documentId: string
): Promise<{ viewUrl: string, downloadUrl?: string }> {
  return apiGet<{ viewUrl: string, downloadUrl?: string }>(`/employees/${employeeId}/documents/${documentId}/urls`);
}
